import React, { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { normalizeApiError } from '../../api/apiError';
import { AppButton } from '../../components/AppButton';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { ScreenHeader } from '../../components/ScreenHeader';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { createOrganizationHrAdmin } from '../../services/managedOrganizationService';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { runOperation } from '../../utils/operationFeedback';

type Props = NativeStackScreenProps<RootStackParamList, 'OrganizationHrAdminCreate'>;
export function OrganizationHrAdminCreateScreen({ navigation, route }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const [form, setForm] = useState({ firstName: '', lastName: '', username: '', email: '', temporaryPassword: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  if (!auth.roles?.includes('SUPER_ADMIN')) return <UnauthorizedScreen />;
  const set = (key: keyof typeof form, value: string) => setForm(current => ({ ...current, [key]: value }));
  const submit = async () => {
    if (!form.firstName.trim() || !form.username.trim() || !form.email.trim() || !form.temporaryPassword) {
      setError('First name, username, email, and temporary password are required.'); return;
    }
    if (form.temporaryPassword !== form.confirmPassword) { setError('Passwords do not match.'); return; }
    setSaving(true);
    try {
      await runOperation('add', () => createOrganizationHrAdmin(route.params.organizationId, form));
      navigation.goBack();
    } catch (caught) {
      setError(normalizeApiError(caught).message);
    }
    finally { setSaving(false); }
  };
  return <AppScreen webLayout="narrow" scroll><ScreenHeader title="Create HR Admin" onBack={navigation.goBack} />
    <View style={styles.body}>
      <Text style={[styles.organization, { color: palette.textSecondary }]}>{route.params.organizationName}</Text>
      <AppInput label="First Name *" value={form.firstName} onChangeText={v => set('firstName', v)} autoCapitalize="words" />
      <AppInput label="Last Name" value={form.lastName} onChangeText={v => set('lastName', v)} autoCapitalize="words" />
      <AppInput label="Username *" value={form.username} onChangeText={v => set('username', v)} autoCapitalize="none" textContentType="username" />
      <AppInput label="Email *" value={form.email} onChangeText={v => set('email', v)} autoCapitalize="none" keyboardType="email-address" textContentType="emailAddress" />
      <AppInput label="Temporary Password *" value={form.temporaryPassword} onChangeText={v => set('temporaryPassword', v)} secureTextEntry textContentType="newPassword" />
      <AppInput label="Confirm Password *" value={form.confirmPassword} onChangeText={v => set('confirmPassword', v)} secureTextEntry textContentType="newPassword" />
      <Text style={[styles.hint, { color: palette.textSecondary }]}>At least 8 characters with uppercase, lowercase, number, and symbol. The user must change it after login.</Text>
      {error ? <Text accessibilityRole="alert" style={[styles.error, { color: palette.error }]}>{error}</Text> : null}
      <View style={styles.actions}><AppButton title="Cancel" variant="secondary" onPress={navigation.goBack} style={styles.flex} />
        <AppButton title="Create HR Admin" onPress={submit} loading={saving} style={styles.flex} /></View>
    </View></AppScreen>;
}
const styles = StyleSheet.create({
  body: { padding: spacing.lg, gap: spacing.md }, organization: { ...typography.body },
  hint: { ...typography.caption }, error: { ...typography.caption },
  actions: { flexDirection: 'row', gap: spacing.sm }, flex: { flex: 1 },
});
